// Wave 4b — client-side registry of in-flight async-convergence batches.
//
// The async create endpoint returns 202 with a ``batch_id`` and the
// real jobs are built by a scheduler daemon seconds-to-minutes later.
// Between «accepted» and «series appear in the /batches poll» there is
// no server-side row the Feed can show, so we remember the batch_id
// locally and render a «готовлю N/M» card that polls
// ``/series/prep/{batch_id}`` until it's DONE/PARTIAL.
//
// Persisted in ``localStorage`` so the card survives a Telegram WebView
// reload (Telegram aggressively re-mounts the view). Every accessor is
// wrapped in ``try/catch`` — Safari private mode throws on access and a
// dead registry must never crash the Feed.

const STORAGE_KEY = 'gnss-qa:active-preps';

/** One in-flight async-convergence batch awaiting background prep. */
export interface ActivePrep {
  batch_id: string;
  /** Stations enqueued for prep (== the 202's ``total``). The card
   *  shows «готовлю prepared/total». */
  total: number;
  /** ISO timestamp of when the 202 came back — used to evict stale
   *  entries (a batch whose daemon died) after {@link MAX_AGE_MS}. */
  created_at: string;
}

/** Drop registry entries older than this even if never marked DONE, so
 *  a daemon crash / lost batch can't pin a ghost card forever. 6h is
 *  far beyond the worst realistic prep time (minutes). */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

function read(): ActivePrep[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ActivePrep =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as ActivePrep).batch_id === 'string' &&
        typeof (e as ActivePrep).total === 'number' &&
        typeof (e as ActivePrep).created_at === 'string',
    );
  } catch {
    return [];
  }
}

function write(entries: ActivePrep[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* ignore storage failures (private mode / quota) */
  }
}

/** Live list, newest first, with stale entries pruned. Pruning happens
 *  on read so a reload after a crash self-heals the registry. */
export function listActivePreps(nowMs: number = Date.now()): ActivePrep[] {
  const fresh = read().filter((e) => {
    const t = Date.parse(e.created_at);
    return Number.isFinite(t) && nowMs - t < MAX_AGE_MS;
  });
  return fresh.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

/** Register a freshly-accepted batch. Idempotent on ``batch_id`` so a
 *  double-submit doesn't render two cards. */
export function addActivePrep(entry: ActivePrep): void {
  const others = read().filter((e) => e.batch_id !== entry.batch_id);
  write([entry, ...others]);
}

/** Forget a batch once its prep finished (or the operator dismissed
 *  it). The prepared series then live in the normal /batches poll. */
export function removeActivePrep(batchId: string): void {
  write(read().filter((e) => e.batch_id !== batchId));
}
